import React, { useState } from 'react';
import { ExternalLink, Maximize2, Minimize2 } from 'lucide-react';
import { cn } from '@/lib/utils';

import { edsComponentLibrary, edsRemoteElements } from '@/lib/eds-component-library';
import { UIResourceRenderer as MCPUIResourceRenderer } from '@mcp-ui/client';
import type { UIResource } from '@/types/chat';

// Enable EDS styling for MCP UI resources
const evoStyling = true;

interface UIResourceRendererProps {
  resource: UIResource;
  className?: string;
}

export const UIResourceRenderer: React.FC<UIResourceRendererProps> = ({ 
  resource,
  className 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { uri, mimeType, text, blob, _meta } = resource.resource;

  // Determine content type from MIME type
  const isHtml = mimeType === 'text/html';
  const isUrl = mimeType === 'text/uri-list';
  const isRemoteDom = mimeType.startsWith('application/vnd.mcp-ui.remote-dom');

  // Decode content
  const content = blob ? atob(blob) : text || '';

  // Get title from metadata
  const title = _meta?.title;
  const description = _meta?.description;
  
  // DEBUG LOGGING
  console.log('🎨 [UIResourceRenderer] Rendering resource:', {
    uri,
    mimeType,
    hasText: !!text,
    hasBlob: !!blob,
    metadata: _meta
  });

  // Render HTML content in sandboxed iframe
  if (isHtml) {
    // Agent should generate HTML with correct colors based on evoStyling flag in tool description
    const htmlContent = content;
    
    return (
      <div className={cn('ui-resource-container my-2', className)}>
        {(title || description) && (
          <div className="mb-2 space-y-1">
            {title && (
              <div className="text-xs font-semibold text-muted-foreground flex items-center gap-2">
                <span>{title}</span>
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="ml-auto hover:text-foreground transition-colors"
                  title={isExpanded ? 'Minimize' : 'Maximize'}
                >
                  {isExpanded ? (
                    <Minimize2 className="w-3 h-3" />
                  ) : (
                    <Maximize2 className="w-3 h-3" />
                  )}
                </button>
              </div>
            )}
            {description && (
              <div className="text-xs text-muted-foreground/80">
                {description}
              </div>
            )}
          </div>
        )}
        <div className={cn(
          'relative rounded-lg border bg-background overflow-hidden transition-all',
          isExpanded ? 'fixed inset-4 z-50 bg-background' : ''
        )}>
          {isExpanded && (
            <div className="absolute top-2 right-2 z-10">
              <button
                onClick={() => setIsExpanded(false)}
                className="bg-background/90 hover:bg-background border rounded-md p-2 shadow-lg"
                title="Minimize"
              >
                <Minimize2 className="w-4 h-4" />
              </button>
            </div>
          )}
          <iframe
            srcDoc={htmlContent}
            sandbox="allow-scripts allow-same-origin"
            className="w-full"
            style={{ 
              minHeight: isExpanded ? '100%' : '300px',
              maxHeight: isExpanded ? '100%' : 'none',
              height: isExpanded ? '100%' : '500px',
              border: 'none',
              width: '100%'
            }}
            title={title || 'UI Content'}
          />
        </div>
      </div>
    );
  }

  // Render external URL in sandboxed iframe
  if (isUrl) {
    return (
      <div className={cn('ui-resource-container my-2', className)}>
        {(title || description) && (
          <div className="mb-2 space-y-1">
            {title && (
              <div className="text-xs font-semibold text-muted-foreground flex items-center gap-2">
                <span>{title}</span>
                <a
                  href={content}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-auto hover:text-foreground transition-colors"
                  title="Open in new tab"
                >
                  <ExternalLink className="w-3 h-3" />
                </a>
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="hover:text-foreground transition-colors"
                  title={isExpanded ? 'Minimize' : 'Maximize'}
                >
                  {isExpanded ? (
                    <Minimize2 className="w-3 h-3" />
                  ) : (
                    <Maximize2 className="w-3 h-3" />
                  )}
                </button>
              </div>
            )}
            {description && (
              <div className="text-xs text-muted-foreground/80">
                {description}
              </div>
            )}
          </div>
        )}
        <div className={cn(
          'relative rounded-lg border bg-background overflow-hidden transition-all',
          isExpanded ? 'fixed inset-4 z-50' : ''
        )}>
          {isExpanded && (
            <div className="absolute top-2 right-2 z-10">
              <button
                onClick={() => setIsExpanded(false)}
                className="bg-background/90 hover:bg-background border rounded-md p-2 shadow-lg"
                title="Minimize"
              >
                <Minimize2 className="w-4 h-4" />
              </button>
            </div>
          )}
          <iframe
            src={content}
            sandbox="allow-scripts allow-same-origin allow-forms"
            className="w-full"
            style={{ 
              minHeight: isExpanded ? '100%' : '400px',
              maxHeight: isExpanded ? '100%' : '800px',
              height: isExpanded ? '100%' : 'auto'
            }}
            title={title || 'External Content'}
          />
        </div>
      </div>
    );
  }

  // Remote DOM - render with EDS component library
  if (isRemoteDom) {
    return (
      <div className={cn('ui-resource-container my-2', className)}>
        {(title || description) && (
          <div className="mb-2 space-y-1">
            {title && (
              <div className="text-xs font-semibold text-muted-foreground flex items-center gap-2">
                <span>{title}</span>
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="ml-auto hover:text-foreground transition-colors"
                  title={isExpanded ? 'Minimize' : 'Maximize'}
                >
                  {isExpanded ? (
                    <Minimize2 className="w-3 h-3" />
                  ) : (
                    <Maximize2 className="w-3 h-3" />
                  )}
                </button>
              </div>
            )}
            {description && (
              <div className="text-xs text-muted-foreground/80">
                {description}
              </div>
            )}
          </div>
        )}
        <div className={cn(
          'relative rounded-lg border bg-background overflow-hidden transition-all',
          isExpanded ? 'fixed inset-4 z-50 bg-background' : ''
        )}>
          {isExpanded && (
            <div className="absolute top-2 right-2 z-10">
              <button
                onClick={() => setIsExpanded(false)}
                className="bg-background/90 hover:bg-background border rounded-md p-2 shadow-lg"
                title="Minimize"
              >
                <Minimize2 className="w-4 h-4" />
              </button>
            </div>
          )}
          <div className={cn(
            'p-4',
            isExpanded ? 'h-full overflow-auto' : ''
          )}>
            {(() => {
              return null;
            })()}
            <MCPUIResourceRenderer
              resource={resource.resource}
              remoteDomProps={evoStyling ? {
                library: edsComponentLibrary as any,
                remoteElements: edsRemoteElements,
              } : undefined}
            />
          </div>
        </div>
      </div>
    );
  }

  // Fallback for unknown types
  return (
    <div className={cn('ui-resource-container my-2', className)}>
      <div className="text-sm text-muted-foreground p-4 border rounded-lg bg-muted/50">
        <div className="font-semibold mb-1">Unsupported Resource</div>
        <div className="text-xs">
          Resource type <code className="bg-background px-1 py-0.5 rounded">{mimeType}</code> is not supported.
        </div>
        {uri && (
          <div className="text-xs mt-2 opacity-70">
            URI: {uri}
          </div>
        )}
      </div>
    </div>
  );
};
